import firebase from "../store/firebase";
import { TRANSCRIPT_EXCERPT_LENGTH, TRANSCRIPT_SIGNATURE_WINDOW, TRANSCRIPT_DUP_NGRAM_N, TRANSCRIPT_DUP_SIMILARITY_THRESHOLD, TRANSCRIPT_DUP_LENGTH_TOLERANCE, TRANSCRIPT_DUP_MAX_CANDIDATES, TRANSCRIPT_DUP_CHAR_SIM_THRESHOLD } from "../constants";

function normalizeForKey(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function simpleHash(input) {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

function compactNoSpaces(text) {
  return String(text || "").replace(/\s+/g, "");
}

function takeEveryNth(input, step) {
  if (step <= 1) return input;
  let out = "";
  for (let i = 0; i < input.length; i += step) {
    out += input[i];
  }
  return out;
}

export function computeTranscriptExcerpt(transcript, preferredLength = TRANSCRIPT_EXCERPT_LENGTH) {
  const text = String(transcript || "").trim();
  if (!text) {
    return { first: "", last: "", length: 0, sigFirstNoSpace200_every5: "", sigLastNoSpace200_every5: "" };
  }
  const safeLength = Math.max(10, Math.min(preferredLength, Math.floor(text.length / 2)));
  const first = text.slice(0, safeLength);
  const last = text.slice(-safeLength);

  // Compute compact signatures from the raw transcript (not the smaller excerpt)
  const noSpace = compactNoSpaces(text);
  const first200 = noSpace.slice(0, TRANSCRIPT_SIGNATURE_WINDOW);
  const last200 = noSpace.slice(-TRANSCRIPT_SIGNATURE_WINDOW);
  const sigFirstNoSpace200_every5 = takeEveryNth(first200, 5);
  const sigLastNoSpace200_every5 = takeEveryNth(last200, 5);

  return { first, last, length: safeLength, sigFirstNoSpace200_every5, sigLastNoSpace200_every5 };
}

function buildExcerptKey(excerpt) {
  // v1 basis (backward compatibility)
  const v1basis = `${normalizeForKey(excerpt.first)}||${normalizeForKey(excerpt.last)}|${excerpt.length}`;
  // v2 basis: compact signatures using no-space and every 5th char on first/last 200 chars
  const v2basis = `f:${excerpt.sigFirstNoSpace200_every5}|l:${excerpt.sigLastNoSpace200_every5}|len:${excerpt.length}`;
  // Prefer v2 for new keys
  return `v2_${simpleHash(v2basis)}_fallback_${simpleHash(v1basis)}`;
}

// -----------------------------
// Similarity utilities (local)
// -----------------------------
function normalizeForSimilarity(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildWordNgrams(text, n = 2) {
  const words = normalizeForSimilarity(text).split(' ').filter(Boolean);
  if (words.length < n) {
    return new Set();
  }
  const grams = new Set();
  for (let i = 0; i <= words.length - n; i++) {
    grams.add(words.slice(i, i + n).join(' '));
  }
  return grams;
}

function jaccardSimilarity(setA, setB) {
  if (!setA.size && !setB.size) return 1;
  if (!setA.size || !setB.size) return 0;
  let inter = 0;
  for (const item of setA) if (setB.has(item)) inter++;
  const union = setA.size + setB.size - inter || 1;
  return inter / union;
}

// Simple normalized Levenshtein similarity for short windows
function charLevelSimilarity(a, b) {
  const s = normalizeForSimilarity(a);
  const t = normalizeForSimilarity(b);
  const m = s.length;
  const n = t.length;
  if (m === 0 && n === 0) return 1;
  if (m === 0 || n === 0) return 0;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  const distance = dp[m][n];
  const maxLen = Math.max(1, Math.max(m, n));
  // Similarity in [0,1]
  return 1 - distance / maxLen;
}

async function findNearDuplicateBySimilarity(db, targetExcerpt, opts = {}) {
  const {
    lengthTolerance = 0.15,
    ngramN = 2,
    threshold = 0.92,
    maxCandidates = 500
  } = opts;

  // Build target n-grams from the same window we store (first+last)
  const targetCombined = `${targetExcerpt.first} ${targetExcerpt.last}`;
  const targetNgramsCombined = buildWordNgrams(targetCombined, ngramN);
  const targetNgramsFirst = buildWordNgrams(targetExcerpt.first, ngramN);
  const targetNgramsLast = buildWordNgrams(targetExcerpt.last, ngramN);
  if (targetNgramsCombined.size === 0 && targetNgramsFirst.size === 0 && targetNgramsLast.size === 0) {
    return { isDuplicate: false, bestScore: 0, matchKey: null };
  }

  try {
    const minLen = Math.max(1, Math.floor(targetExcerpt.length * (1 - lengthTolerance)));
    const maxLen = Math.ceil(targetExcerpt.length * (1 + lengthTolerance));

    // Query by excerpt length to reduce scan size
    const ref = db.ref('transcriptExcerpts').orderByChild('length').startAt(minLen).endAt(maxLen).limitToLast(maxCandidates);
    const snap = await ref.once('value');
    if (!snap.exists()) {
      return { isDuplicate: false, bestScore: 0, matchKey: null };
    }

    let bestScore = 0;
    let matchKey = null;
    snap.forEach(child => {
      const val = child.val() || {};
      const combined = `${val.first || ''} ${val.last || ''}`;
      const gramsCombined = buildWordNgrams(combined, ngramN);
      const gramsFirst = buildWordNgrams(val.first || '', ngramN);
      const gramsLast = buildWordNgrams(val.last || '', ngramN);

      const scoreCombined = jaccardSimilarity(targetNgramsCombined, gramsCombined);
      const scoreFirst = jaccardSimilarity(targetNgramsFirst, gramsFirst);
      const scoreLast = jaccardSimilarity(targetNgramsLast, gramsLast);
      // Character-level strict check for edge windows (first/last)
      const charSimFirst = charLevelSimilarity(targetExcerpt.first, val.first || '');
      const charSimLast = charLevelSimilarity(targetExcerpt.last, val.last || '');
      const strictEdgeHit = Math.max(charSimFirst, charSimLast) >= (TRANSCRIPT_DUP_CHAR_SIM_THRESHOLD || 0.98);

      const score = strictEdgeHit ? 1 : Math.max(scoreCombined, scoreFirst, scoreLast);

      if (score > bestScore) {
        bestScore = score;
        matchKey = child.key;
      }
    });

    return { isDuplicate: bestScore >= threshold, bestScore, matchKey };
  } catch (_) {
    // On any error, be permissive and do not block
    return { isDuplicate: false, bestScore: 0, matchKey: null };
  }
}

export async function checkTranscriptDuplicate(transcript, preferredLength) {
  const excerpt = computeTranscriptExcerpt(transcript, preferredLength);
  const key = buildExcerptKey(excerpt);
  const ref = firebase.database.ref(`transcriptExcerpts/${key}`);
  const snapshot = await ref.once("value");
  if (snapshot.exists()) {
    return { isDuplicate: true, key, excerpt };
  }
  // Backward-compat: also check a pure v1 key in case older records exist
  const v1Key = `v1_${simpleHash(`${normalizeForKey(excerpt.first)}||${normalizeForKey(excerpt.last)}|${excerpt.length}`)}`;
  const refV1 = firebase.database.ref(`transcriptExcerpts/${v1Key}`);
  const snapV1 = await refV1.once("value");
  if (snapV1.exists()) {
    return { isDuplicate: true, key: v1Key, excerpt };
  }

  // Fuzzy near-duplicate check using normalized bigram Jaccard on stored first+last windows
  const { isDuplicate, bestScore } = await findNearDuplicateBySimilarity(
    firebase.database,
    excerpt,
    {
      lengthTolerance: TRANSCRIPT_DUP_LENGTH_TOLERANCE,
      ngramN: TRANSCRIPT_DUP_NGRAM_N,
      threshold: TRANSCRIPT_DUP_SIMILARITY_THRESHOLD,
      maxCandidates: TRANSCRIPT_DUP_MAX_CANDIDATES
    }
  );

  return { isDuplicate, key, excerpt, similarityScore: bestScore };
}

export async function saveTranscriptExcerpt(key, excerpt, metadata = {}) {
  const ref = firebase.database.ref(`transcriptExcerpts/${key}`);
  const payload = {
    first: excerpt.first,
    last: excerpt.last,
    length: excerpt.length,
    // Store compact signatures for potential future matching/analytics
    sigFirstNoSpace200_every5: excerpt.sigFirstNoSpace200_every5,
    sigLastNoSpace200_every5: excerpt.sigLastNoSpace200_every5,
    createdAt: Date.now(),
    ...metadata
  };
  // Debug logging for visibility of stored values
  try {
    console.log('[TranscriptExcerpts] save', {
      key,
      length: payload.length,
      first: payload.first,
      last: payload.last,
      sigFirstNoSpace200_every5: payload.sigFirstNoSpace200_every5,
      sigLastNoSpace200_every5: payload.sigLastNoSpace200_every5
    });
  } catch (e) {}
  await ref.set(payload);
  return payload;
}

export async function saveTranscriptExcerptIfUnique(transcript, metadata = {}, preferredLength) {
  const { isDuplicate, key, excerpt } = await checkTranscriptDuplicate(transcript, preferredLength);
  if (isDuplicate) {
    return { saved: false, isDuplicate: true, key };
  }
  await saveTranscriptExcerpt(key, excerpt, metadata);
  return { saved: true, isDuplicate: false, key };
}




